import {ChangeEvent, Dispatch, SetStateAction} from 'react';

import {ProductCategoryEnum, ProductVariant, Subscription} from '#apollo/schema';
import {Supplement} from '#constant/products';
import {Colors} from '#theme';

export enum SubscriptionManagerState {
  DESELECTED = 'deselected',
  PAUSED = 'paused',
  ACTIVE = 'active plan',
  UPDATE = 'update plan',
  PENDING = 'pending',
}

export interface SubscriptionManagerCardProps {
  customerType: CustomerType;
  state: SubscriptionManagerState;
  productVariants: ProductVariant[];
  initialState: SubscriptionManagerStateType | null;
  setInitialState: Dispatch<SetStateAction<SubscriptionManagerStateType | null>>;
  pendingChanges: SubscriptionManagerStateType | null;
  setPendingChanges: Dispatch<SetStateAction<SubscriptionManagerStateType | null>>;
  onChangeSubscription: ({
    hasMembership,
    initialState,
    setPendingChanges,
  }: onChangeSubscriptionProps) => void;
  onCancelPlan: () => void;
}

export enum PromoCodeAction {
  ADD = 'add',
  REMOVE = 'remove',
  CLEAR = 'clear',
}

export interface SubscriptionManagerContextProps {
  selectedSupplements: {[key: string]: boolean};
  setSelectedSupplements: React.Dispatch<React.SetStateAction<{[key: string]: boolean}>>;
  selectedSupplementsCount: number;
  initialState: SubscriptionManagerStateType | null;
  setInitialState: React.Dispatch<React.SetStateAction<SubscriptionManagerStateType | null>>;
  pendingChanges: SubscriptionManagerStateType | null;
  setPendingChanges: React.Dispatch<React.SetStateAction<SubscriptionManagerStateType | null>>;
  getInitialSubscriptionManagerState: (
    props: InitialSubscriptionStateProps,
  ) => SubscriptionManagerStateType;
  onChangeSubscription: (props: onChangeSubscriptionProps) => void;
  onChangeSupplement: (
    supplement: ManageProductType,
    productVariants: ProductVariant[],
    customerType: CustomerType,
    subscription: Partial<Subscription>,
  ) => (event: ChangeEvent<HTMLInputElement>) => void;
  appliedPromoCodes: string[];
  setAppliedPromoCodes: Dispatch<SetStateAction<string[]>>;
  discountsToSimulate: string[];
  setDiscountsToSimulate: Dispatch<SetStateAction<string[]>>;
  updatePromoCodes: (promoCode: string, action: PromoCodeAction) => void;
  openUpdateConfirmationModal: boolean;
  setOpenUpdateConfirmationModal: Dispatch<SetStateAction<boolean>>;
  hasAutoAppliedPromoCodes: boolean;
  setHasAutoAppliedPromoCodes: Dispatch<SetStateAction<boolean>>;
  alreadyAppliedPromoCodeError: boolean;
  setAlreadyAppliedPromoCodeError: Dispatch<SetStateAction<boolean>>;
  setHasAutoAppliedPromoCodesError: Dispatch<SetStateAction<boolean>>;
}

export interface GetSubscriptionManagerCardDataProps {
  customerType: CustomerType;
  state: SubscriptionManagerState;
  productVariants: ProductVariant[];
}

export interface GetSubscriptionManagerCardDataReturn {
  supplementTotalValue: number;
  supplementTotalValueDiscounted: number;
  membershipTotalValue: number;
  membershipTotalValueDiscounted: number;
  textColor: Colors;
  isDiscounted: boolean;
  title: string;
  activeSubscription: boolean;
  discount: number;
  supplements: Supplement[];
  selectedSupplements: Record<string, boolean>;
  onChangeSupplement: (
    supplement: ManageProductType,
    productVariants: ProductVariant[],
    customerType: CustomerType,
    subscription: Partial<Subscription>,
  ) => (event: ChangeEvent<HTMLInputElement>) => void;
  selectedSupplementsCount: number;
  initialState: any;
  setInitialState: (state: any) => void;
  pendingChanges: any;
  undoChanges: () => void;
  supplementRenderList: ManageProductType[];
  onChangeSubscription: (props: onChangeSubscriptionProps) => void;
  startingAtValue: number;
  handleTrackClick: (actionName: string) => void;
  appliedPromoCodes: string[];
  discountsToSimulate: string[];
  openUpdateConfirmationModal: boolean;
  setOpenUpdateConfirmationModal: Dispatch<SetStateAction<boolean>>;
  hasAutoAppliedPromoCodes: boolean;
  setHasAutoAppliedPromoCodes: Dispatch<SetStateAction<boolean>>;
  alreadyAppliedPromoCodeError: boolean;
  setAlreadyAppliedPromoCodeError: Dispatch<SetStateAction<boolean>>;
  setHasAutoAppliedPromoCodesError: Dispatch<SetStateAction<boolean>>;
}

export interface TransformVariantToProductProps {
  variant: ProductVariant;
  supplement: Supplement | undefined;
  membershipIncluded?: boolean;
}

export interface HeaderSubscriptionProps {
  title: string;
  textColor: Colors;
  isDiscounted: boolean;
  state: SubscriptionManagerState;
  totalValue: number;
  totalValueWithDiscount: string;
  startingAtValue: number;
}

export interface BenefitsListProps {
  customerType: CustomerType;
}

export interface InternalBenefitsListProps {
  quantitySupplementSelected: number;
  selected: boolean;
}

export interface BenefitItemProps {
  children: string;
  selected: boolean;
}

export interface ProductRowProps {
  image: string;
  title: string;
  value: number;
  subscribed: boolean;
  discount: number;
  membershipIncluded: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

export interface SubscriptionManagerStateType {
  customerType: CustomerType;
  state: SubscriptionManagerState;
  subscriptionStatus: string;
  benefitDiscount: number;
  totalValue: number;
  products: ManageProductType[];
  hasActiveSubscription?: boolean;
  hasMembership?: boolean;
  nextBillingDateAt: string;
  initialProducts: string[];
  removeProducts: string[];
  addProducts: string[];
  subscriptionId: string;
  addDiscountCodes: string[];
}

export type ManageProductType = {
  variantId: string;
  price: number;
  imageURL: string;
  title: string;
  membershipIncluded?: boolean;
  tid: string;
  category: ProductCategoryEnum;
  displayOrder?: number;
};

export interface SupplementRenderListProps {
  customerType: CustomerType;
  productVariants: ProductVariant[];
  activeSubscription: boolean;
  selectedSupplements: {[key: string]: boolean};
}

export interface InitialSubscriptionStateProps {
  subscription: Partial<Subscription>;
  productVariants: ProductVariant[];
}

export interface onChangeSubscriptionProps {
  hasMembership: boolean;
  initialState: SubscriptionManagerStateType;
  setPendingChanges: Dispatch<SetStateAction<SubscriptionManagerStateType | null>>;
  productVariants: ProductVariant[];
  customerType: CustomerType;
}

export type ManagePlanParams = {
  add?: string;
  discount?: string;
};

export interface GetHighestPricedSupplementTidProps {
  selectedVariants: ManageProductType[];
  activeSubscription: boolean;
  customerType: CustomerType;
  productVariants: ProductVariant[];
}

export type DiscountRule = {
  minCount: number;
  maxCount?: number;
  discount: number;
};

export type CustomerType = 'membership' | 'subscription';
